import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useDraft } from '../../state/draft';
import { STARTER_PROMPTS } from '@/webapp/plan/scaffolds';
import { creditBalance } from '../../api/billing';
import { buildsIncluded } from '../../state/trial';
import { useFunnelStep } from '../../analytics/funnel';
import { Button, Screen } from '../../ui';

/** Long enough for any real sentence; short enough that a link can't paste in an essay. */
const MAX_LINKED_IDEA = 1000;

/**
 * The front door, ported from src/app/(onboarding)/create.tsx. One idea in, nothing else.
 *
 * The starters are deliberately specific — "an event app" produces mush, "RSVP for a
 * birthday dinner" produces an app — so tapping one goes straight to a plan.
 */
export default function Idea() {
  const navigate = useNavigate();
  const setDescription = useDraft((s) => s.setDescription);
  const setAppPlan = useDraft((s) => s.setPlan);
  const stored = useDraft((s) => s.description);
  // saddle-sites.com's hero links here with the sentence the visitor already typed. It
  // only fills the field — nothing is planned or spent until they press Continue.
  const [params] = useSearchParams();
  /*
   * Or it arrives in router state: the journey needs an account, so an ad's visitor signs up
   * first, and the sign-in that brings them back hands the idea over there rather than in the
   * address, where the by-then-live pixel would read it (shell/returnTo.ts).
   */
  const location = useLocation();
  const handedOver = (location.state as { idea?: unknown } | null)?.idea;
  const fromLink = (params.get('idea') ?? (typeof handedOver === 'string' ? handedOver : undefined))
    ?.trim()
    .slice(0, MAX_LINKED_IDEA);
  const [idea, setIdea] = useState(fromLink || stored);
  /*
   * Whether they came with their idea already written — an ad, or the landing site's hero. Read
   * once: the effect below takes the sentence out of the address, and the screen must not change
   * its mind about who it is talking to when it does.
   */
  const [linked] = useState(() => Boolean(fromLink));
  useFunnelStep('idea_viewed');

  /*
   * "Free" is said only where it is true. The server plans for nothing for an account with no
   * subscription and too few credits for the plan and its build — up to five a day, and a sixth is
   * refused, never charged (infra/lambda/shared/paywall.ts). A subscriber's plan is paid for in
   * credits. So the word waits for the balance, and a read that fails leaves it out.
   */
  const [planFree, setPlanFree] = useState(false);
  useEffect(() => {
    if (!linked) return;
    let live = true;
    void creditBalance()
      .then((b) => {
        if (live) setPlanFree(!buildsIncluded(b) && b.subscribed !== true);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [linked]);

  /*
   * Read once, then taken out of the address bar.
   *
   * The sentence is the person's own words — an address, a child's name — and while it sits
   * in the URL, every PageView the Meta pixel sends carries the full URL with it. The privacy
   * policy says Meta is never sent prompts. Only `idea` goes: `fbclid` and `utm_*` are the
   * ad attribution the pixel exists for, and they stay. The field already holds the text.
   * ConsentBanner also waits for this param to be gone, so neither side relies on the order
   * the two effects run in.
   *
   * The idea handed over in router state is taken out of the history entry the same way. Left
   * there, the browser's Back from the plan put the ad's sentence back in the field over the
   * person's edit, and Continue from there threw the edit away (review, 2026-09-29). The field
   * holds the text; the draft holds it once they continue.
   */
  useEffect(() => {
    if (!params.has('idea') && typeof handedOver !== 'string') return;
    const next = new URLSearchParams(params);
    next.delete('idea');
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: null },
    );
    // Once, on arrival: the link is read a single time and the field owns the text after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (text: string): void => {
    const goal = text.trim();
    if (!goal) return;
    setDescription(goal);
    // Cleared so the plan screen always starts from a scaffold built for THIS idea,
    // never a plan left over from a previous run.
    setAppPlan(null);
    navigate('/new/plan');
  };

  return (
    <Screen title="What do you want to make?">
      {linked ? (
        // Their sentence is already in the box; what they need is what happens next.
        <p className="type-body idea-sub">
          {planFree
            ? 'Saddle will plan this for you — free — then build it when you’re ready.'
            : 'Saddle will plan this for you, then build it when you’re ready.'}
        </p>
      ) : (
        <p className="type-body idea-sub">
          Describe it in a line. You&rsquo;ll see the plan before anything is built.
        </p>
      )}
      {/* The phone attaches photos here; the web does it two steps on, and says so (F11). */}
      <p className="type-caption idea-sub">
        Got photos, or documents like a menu or a schedule? You can add them in a moment.
      </p>

      <label className="type-caption field-label" htmlFor="idea">
        Describe the app you want
      </label>
      <textarea
        id="idea"
        className="field idea-input"
        value={idea}
        rows={3}
        onChange={(e) => setIdea(e.target.value)}
        placeholder="An RSVP page for my sister's baby shower…"
      />
      {/* Named for what it does next: "Continue" said nothing about a plan, or that nothing is built yet. */}
      <Button title="Plan my app" onPress={() => go(idea)} disabled={idea.trim().length === 0} />

      {/* Somebody who arrived with their own idea is not offered eight others — on a phone they
          pushed "Plan my app" up into a crowd. Everyone else still gets them. */}
      {!linked && (
        <>
          <h2 className="type-micro section-label">Or start from one of these</h2>
          <div className="starter-grid">
            {STARTER_PROMPTS.map((s) => (
              <button
                key={s.label}
                type="button"
                className="card clay-card pressable starter"
                aria-label={`${s.label}. ${s.prompt}`}
                onClick={() => go(s.prompt)}
              >
                <span className="type-heading">{s.label}</span>
                <span className="type-caption starter-sub">{s.prompt}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </Screen>
  );
}
